<script>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { removeSignature } from 'dashboard/helper/editorHelper';

export default {
  name: 'CreateCannedModal',
  components: { NextButton },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    messageContent: {
      type: String,
      default: '',
    },
    signature: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'created', 'update:show'],
  setup() {
    const shortCode = ref('');
    return { shortCode };
  },
  computed: {
    isShortCodeValid() {
      return this.shortCode.trim().length > 0;
    },
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    messageWithoutSignature() {
      if (!this.signature) {
        return this.messageContent;
      }
      // Remove signature from the message using the helper function
      return removeSignature(this.messageContent, this.signature);
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.shortCode = '';
      }
    },
  },
  methods: {
    async handleCreate() {
      if (!this.isShortCodeValid || !this.messageWithoutSignature.trim()) {
        return;
      }

      try {
        const cannedResponse = await this.$store.dispatch(
          'createCannedResponse',
          {
            short_code: this.shortCode.trim(),
            content: this.messageWithoutSignature.trim(),
          }
        );

        useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.$emit('created', cannedResponse);
        this.$emit('close');
      } catch (error) {
        const errorMessage =
          error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="handleClose">
    <woot-modal-header
      :header-title="$t('CONVERSATION.REPLYBOX.CREATE_CANNED.TITLE')"
    />
    <div class="p-6">
      <div class="mb-4">
        <label
          class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ $t('CONVERSATION.REPLYBOX.CREATE_CANNED.SHORT_CODE_LABEL') }}
        </label>
        <input
          v-model="shortCode"
          type="text"
          class="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
          :placeholder="
            $t('CONVERSATION.REPLYBOX.CREATE_CANNED.SHORT_CODE_PLACEHOLDER')
          "
          @keyup.enter="handleCreate"
        />
      </div>
      <div class="mb-4">
        <label
          class="block mb-2 text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ $t('CONVERSATION.REPLYBOX.CREATE_CANNED.CONTENT_LABEL') }}
        </label>
        <textarea
          :value="messageWithoutSignature"
          readonly
          class="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-300"
          rows="4"
        />
      </div>
    </div>
    <div
      class="flex justify-end gap-2 p-6 border-t border-slate-200 dark:border-slate-700"
    >
      <NextButton faded slate size="small" @click="handleClose">
        {{ $t('CONVERSATION.REPLYBOX.CREATE_CANNED.CANCEL') }}
      </NextButton>
      <NextButton
        :disabled="!isShortCodeValid"
        size="small"
        @click="handleCreate"
      >
        {{ $t('CONVERSATION.REPLYBOX.CREATE_CANNED.CREATE') }}
      </NextButton>
    </div>
  </woot-modal>
</template>
